import './styles/TermsConditions_E.scss';

import { useEffect, useMemo, useRef, useState } from 'react';

import AppleLoading from '../../../../components/AppleLoading';
import { Stack } from '../../../../components/Stack';
import { UnexpectedErrorWebsheet } from '../../../../components/UnexpectedErrorWebsheet';
import { parseError } from '../../../../helpers/errorUtils';
import { useClassHelper } from '../../../../hooks/useClassHelper';
import { WebsheetTemplateProps } from '../../../../types/websheet';
import { TermsAndConditionsBase } from '../../components/TermsAndConditionsBase';
import { DATADOG_ACTIONS } from '../../constants';
import {
  isTextChildrenEmpty,
  replaceTokens,
  replaceTokensInContent,
} from '../../helpers';
import { trackSdkCall } from '../../helpers/trackSdkCall';
import { useDataDogActions } from '../../hooks/useDataDogActions';
import { useGetStyles } from '../../hooks/useGetStyles';
import { useLocalisation } from '../../hooks/useLocalisation';
import { useVisibilityCheck } from '../../hooks/useVisibilityCheck';
import { useGetESIMTransferConfig } from './hooks/useGetESIMTransferConfig';

const componentName = 'c-TermsConditionsE';

const DATA_DOG_CONTEXT = 'esim_transfer';

type AgreeDisagreeText = {
  agree?: string;
  disagree?: string;
  title?: string;
};

type Props = WebsheetTemplateProps<AgreeDisagreeText>;

export const TermsConditionsE = ({
  t,
  values,
  currentLocale,
  deviceSdk,
  portalMessenger,
  isPortalPreview,
  text, // currentText from PreviewContainer.tsx
  approveHandler,
  state,
  services,
}: Props) => {
  const {
    data: transferConfigData,
    isLoading: transferConfigLoading,
    error: transferConfigError,
  } = useGetESIMTransferConfig({
    state,
    services,
    enabled: !isPortalPreview,
  });

  const getStyles = useGetStyles({ values });

  const { localisations } = values;
  const c = useClassHelper(componentName);

  const { actions, dataDogLogger, isPortalTest } = useDataDogActions({
    isPortalPreview,
    actionKey: DATA_DOG_CONTEXT,
  });

  const localisedData = useLocalisation(
    localisations,
    currentLocale,
    dataDogLogger,
    't&c',
  );

  const currentLocalisation = isPortalPreview ? text : localisedData;

  // Single in-flight flag: both buttons disable together, double-submits are
  // blocked, and the active button exposes aria-busy while a handler runs.
  const [isLoading, setIsLoading] = useState(false);

  // Ref attached to the rendered `<main>` of TermsAndConditionsBase via the
  // containerRef prop. No extra wrapper div → layout untouched, DOM checks
  // run against the real rendered root.
  const containerRef = useRef<HTMLElement>(null);

  useVisibilityCheck(containerRef, {
    enabled: !isPortalPreview,
    logger: dataDogLogger,
    errorAction: DATADOG_ACTIONS.errors.template_error,
    flow: 'render',
  });

  // Attach the resolved transfer_type to the DataDog session so every
  // subsequent event is segmentable by it. Skipped in preview/test mode.
  useEffect(() => {
    if (transferConfigData?.transfer_type && !isPortalPreview) {
      dataDogLogger.addGlobal(
        'transfer_type',
        transferConfigData.transfer_type,
      );
    }
  }, [transferConfigData]);

  // Report config GET failures to DataDog with the parsed error shape. Skipped
  // in preview/test mode to avoid polluting production metrics.
  useEffect(() => {
    if (transferConfigError && !isPortalPreview) {
      const { errorCode, errorMessage, statusCode } =
        parseError(transferConfigError);

      dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
        flow: 'load_config',
        statusCode,
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
      });
    }
  }, [transferConfigError]);

  const handleImageError = (url: string) => {
    if (isPortalPreview) {
      return;
    }

    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'render_logo',
      statusCode: 0,
      errorCode: 'IMAGE_LOAD_FAILED',
      errorMessage: `Logo image failed to load: ${url}`,
      imageUrl: url,
    });
  };

  const DEFAULT_CONTENT = [
    {
      type: 'paragraph',
      children: [
        {
          text: t('webSheetPreview.default_content'),
        },
      ],
    },
  ];

  // Device-name placeholders injected into the localised T&C copy. Names come
  // from GET /transfer-sim; an UNKNOWN transfer_type can yield null names, so
  // fall back to a literal (mirrors the watch flow) to avoid rendering a raw
  // `{token}`.
  const deviceNameTokens = useMemo(
    () => ({
      source_device_name:
        transferConfigData?.source_device_name || 'your device',
      target_device_name:
        transferConfigData?.target_device_name || 'your device',
    }),
    [transferConfigData],
  );

  const rawTitle =
    currentLocalisation?.title ?? t('webSheetPreview.default_title');
  const title = replaceTokens(rawTitle, deviceNameTokens);
  const content =
    replaceTokensInContent(
      currentLocalisation?.content ?? DEFAULT_CONTENT,
      deviceNameTokens,
    ) ?? DEFAULT_CONTENT;
  const hasBothFooter =
    currentLocalisation?.agree && currentLocalisation?.disagree;
  const agree = hasBothFooter
    ? currentLocalisation?.agree
    : t('webSheetPreview.footer_agree');
  const disagree = hasBothFooter
    ? currentLocalisation?.disagree
    : t('webSheetPreview.footer_disagree');

  const isContentEmpty = useMemo(() => {
    return isTextChildrenEmpty(content);
  }, [content]);

  const handleDecline = async () => {
    if (isPortalPreview || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      if (portalMessenger) {
        portalMessenger.propagateDecline();
      }

      await deviceSdk.decline();
    } catch (error) {
      const { errorCode, errorMessage, statusCode } = parseError(error);

      dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
        flow: 'decline',
        statusCode,
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
      });
    } finally {
      dataDogLogger.stopSession();
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (isPortalPreview || isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      await approveHandler?.();
    } catch (error) {
      const { errorCode, errorMessage, statusCode } = parseError(error);

      dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
        flow: 'approve',
        statusCode,
        errorCode: errorCode ?? 'UNKNOWN',
        errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (transferConfigLoading) {
    return (
      <div
        className={c('__loading-wrapper')}
        role="status"
        aria-busy="true"
        aria-live="polite"
      >
        <AppleLoading />
      </div>
    );
  }

  if (transferConfigError) {
    return (
      <UnexpectedErrorWebsheet
        title={t('error.title')}
        body={t('error.body')}
        close={t('error.close')}
        onClick={() => {
          if (deviceSdk && deviceSdk.exit) {
            // exit() emits finishFlow + dismissFlow actions; stop the session
            // after so those logs are captured in it.
            trackSdkCall('exit', () => deviceSdk.exit(), dataDogLogger);
            dataDogLogger.stopSession();
            return;
          }

          // SDK method missing — the user's close does nothing. Report the
          // silent failure, then close the session.
          if (!isPortalPreview) {
            dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
              flow: 'sdk_unavailable',
              errorCode: 'SDK_METHOD_MISSING',
              errorMessage: 'deviceSdk.exit is unavailable',
              method: 'exit',
            });
            dataDogLogger.stopSession();
          }
        }}
      />
    );
  }

  return (
    <TermsAndConditionsBase
      containerRef={containerRef}
      onImageError={handleImageError}
      getStyles={getStyles}
      isPortalPreview={isPortalPreview || isPortalTest}
      styling={{
        logoUrl: values?.logoUrl,
        title: {
          text: title,
          fontFamily: values?.fontTitle,
        },
        content: {
          text: isContentEmpty ? DEFAULT_CONTENT : content,
          valueFontParagraph: values?.fontParagraph,
        },
      }}
      footer={
        <Stack direction="row" justify="between" className={c('__footer')}>
          <button
            type="button"
            className={c('__highlight')}
            onClick={handleDecline}
            aria-disabled={isLoading}
            aria-busy={isLoading}
            data-dd-action-name={actions?.decline_button_clicked}
          >
            {disagree}
          </button>
          <button
            type="button"
            className={c('__highlight')}
            onClick={handleApprove}
            aria-disabled={isLoading}
            aria-busy={isLoading}
            data-dd-action-name={actions?.approve_button_clicked}
          >
            {agree}
          </button>
        </Stack>
      }
    />
  );
};
